/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo, useState } from 'react';

import {
  Divider,
  Grid,
  Hidden,
  SelectField,
  SelectOption,
  Tab,
  TabContext,
  TabPanel,
  Tabs,
  Typography2 as Typography,
  breakpoints,
} from '@coursera/cds-core';

import CareerRolePanel from 'bundles/premium-hub/components/career-module/CareerRolePanel';
import Container from 'bundles/premium-hub/components/shared/Container';
import type { CareerRolesModuleData } from 'bundles/premium-hub/types';

import _t from 'i18n!nls/premium-hub';

type Props = {
  moduleData?: CareerRolesModuleData | null;
};

const styles = {
  moduleContainer: css`
    margin-top: var(--cds-spacing-400);
  `,
  tabPanel: css`
    padding-top: var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      padding-top: var(--cds-spacing-200);
    }
  `,
  selectFieldContainer: css`
    width: 100%;
    margin-top: var(--cds-spacing-200);
  `,
  fullWidth: css`
    width: 100%;
  `,
  divider: css`
    margin: calc(var(--cds-spacing-400) + var(--cds-spacing-100)) 0;
  `,
};

const CareerRolesModule = ({ moduleData }: Props) => {
  const [currentRoleId, setCurrentRoleId] = useState(moduleData?.roleItems?.[0]?.roleId);
  const currentRole = useMemo(() => {
    return moduleData?.roleItems?.find((item) => item?.roleId === currentRoleId);
  }, [currentRoleId, moduleData]);

  if (!moduleData || !moduleData.roleItems || !moduleData.roleItems.length) return null;

  const handleTabChange = (newRoleId: string) => {
    setCurrentRoleId(newRoleId);
  };

  return (
    <Container css={styles.moduleContainer}>
      <Grid container>
        <Grid item xs={12}>
          <Typography variant="subtitleLarge" component="h2">
            {moduleData.title}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TabContext value={currentRoleId ?? ''}>
            <Hidden xsDown>
              <Tabs variant="section" aria-label={_t('Career Roles Tabs')} onChange={handleTabChange}>
                {moduleData.roleItems.map((item) => (
                  <Tab key={item.roleId} label={item.roleName ?? ''} value={item?.roleId ?? ''} />
                ))}
              </Tabs>
            </Hidden>
            <Hidden smUp css={styles.selectFieldContainer}>
              <SelectField
                aria-label={_t('Career roles')}
                value={currentRoleId ?? ''}
                onChange={(e) => handleTabChange(e.target.value as string)}
                css={styles.fullWidth}
              >
                {moduleData.roleItems.map((item) => (
                  <SelectOption key={item.roleId} value={item.roleId ?? ''}>
                    {item.roleName}
                  </SelectOption>
                ))}
              </SelectField>
            </Hidden>
            <TabPanel value={currentRoleId ?? ''} css={styles.tabPanel}>
              <CareerRolePanel role={currentRole} />
            </TabPanel>
          </TabContext>
        </Grid>
      </Grid>
      <Divider color="light" orientation="horizontal" css={styles.divider} />
    </Container>
  );
};

export default CareerRolesModule;
